import { applySnapshot, flow, types } from "mobx-state-tree";
import moment from "moment";
import { ErrorModel } from "../../components/common/error";
import { MessageModel } from "../../components/common/message";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { SelectListModel } from "../share";
import agreementServiceAPI from "./AgreementService";
import monthlyReportAPI from "../monthlyreport/MonthlyReportService";
import { ILoanSourcesModel, LoanSourcesModel } from "../share/LoanModel";

export const ObjectiveModel = types
  .model("ObjectiveModel", {
    id: types.optional(types.number, 0),
    code: types.optional(types.string, ""),
    description: types.optional(types.string, ""),
    isSelected: types.optional(types.boolean, false),
  }).actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));

export type IObjectiveModel = typeof ObjectiveModel.Type;

const formatDateToISO = (dateString: any) => {
  if (!dateString) {
    return null;
  }
  
  // ตรวจสอบว่าเป็นรูปแบบ DD/MM/YYYY หรือไม่
  if (dateString.includes('/')) {
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // เดือนใน JavaScript เริ่มจาก 0
      const year = parseInt(parts[2], 10);
      
      const date = new Date(year, month, day);
      return date.toISOString();
    }
  }
  
  // ถ้าไม่ใช่รูปแบบ DD/MM/YYYY ให้ตรวจสอบว่าเป็นวันที่ที่ถูกต้องหรือไม่
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date.toISOString();
  }
  
  return null;
};
export const AgreementDetailModel = types
  .model("AgreementDetailModel", {
    isSelected: customtypes.optional(types.boolean, false),
    isMapped: types.optional(types.boolean, false),
    agreementID: types.maybe(types.number),
    masterAgreement: customtypes.optional(types.string, ""),
    debtType: customtypes.optional(types.string, ""),
    debtSubType: customtypes.optional(types.string, ""),
    interestFormular: customtypes.optional(types.string, ""),
    gftrRefCode: customtypes.optional(types.string, ""),
    description: customtypes.optional(types.string, ""),
    descriptionTH: customtypes.optional(types.string, ""),
    counterparty: customtypes.optional(types.string, ""),
    referenceCode: customtypes.optional(types.string, ""),
    interestRate: customtypes.optional(types.number, 0),
    signDate: customtypes.optional(types.string, ""),
    startDate: customtypes.optional(types.string, ""),
    endDate: customtypes.optional(types.string, ""),
    transactionType: customtypes.optional(SelectListModel, {}),
    incomingDueDate: customtypes.optional(types.string, ""),
    sourceType: customtypes.optional(types.string, ""),
    isGuarantee: customtypes.optional(types.boolean, false),
    loanAmount: customtypes.optional(types.number, 0),
    // actualDueAmount: customtypes.optional(types.number, 0),
    actualDues: customtypes.optional(types.array(LoanSourcesModel), []),
    actualDueDate: customtypes.optional(types.string, ""),
    actualMasterAgreement: customtypes.optional(types.string, ""),
    objective: types.maybeNull(ObjectiveModel),
    planType: types.maybeNull(ObjectiveModel),
    balanceAmount: customtypes.optional(types.number, 0),
    loanCurrency: customtypes.optional(types.string, ""),
    loanAmonthTHB: customtypes.optional(types.number, 0),
    outStandingDebt: customtypes.optional(types.number, 0),
    outStandingDebtTHB: customtypes.optional(types.number, 0),
    incomingDueAmount: customtypes.optional(types.number, 0),
    loanAge: customtypes.optional(types.number, 0),
    organization: customtypes.optional(SelectListModel, {}),
    loading: types.optional(types.boolean, false),
    alert: types.optional(MessageModel, {}),
    error: types.optional(ErrorModel, {})
  })
  .views((self: any) => ({
    get incomingDueAmountAsMillionBaht() {
      return self.incomingDueAmount / 1000000;
    },
    get loanAmountAsMillionBaht() {
      return self.loanAmount / 1000000;
    },
    get transactionTypeStr() {
      return self.transactionType ? self.transactionType.description : "";
    },
    get loanSourceTypeDomestic() {
      return self.actualDues.find((item: ILoanSourcesModel) => {
        return item.sourceType === "L";
      });
    },
    get loanSourceTypeForeignDebt() {
      return self.actualDues.filter(
        (item: ILoanSourcesModel) => item.sourceType !== "L"
      );
    }
  }))
  .actions((self: any) => ({
    createAgreementWithTransaction: flow(function* (agreementTransData: any) {
      try {
        self.loading = true;
        
        // ตรวจสอบว่ามีการกรอกข้อมูลสำคัญครบถ้วนหรือไม่
        if (!self.organization || !self.organization.code) {
          throw new Error("กรุณาระบุหน่วยงาน");
        }
        
        // แปลงวันที่ให้อยู่ในรูปแบบ ISO string
        const signDate = formatDateToISO(self.signDate) || new Date().toISOString();
        const startDate = formatDateToISO(self.startDate) || new Date().toISOString();
        const endDate = formatDateToISO(self.endDate) || signDate;
        const incomingDueDate = formatDateToISO(self.incomingDueDate) || null;
        
        // สร้างข้อมูล Agreement
        const agreementData = {
          agreementID: self.agreementID, // null สำหรับการสร้างใหม่
          organization: {
            id: self.organization.id,
            code: self.organization.code,
            description: self.organization.description
          },
          referenceCode: self.referenceCode,
          gftrRefCode: self.gftrRefCode,
          description: self.description,
          descriptionTH: self.descriptionTH,
          masterAgreement: self.masterAgreement,
          interestRate: self.interestRate,
          debtType: self.debtType,
          debtSubType: self.debtSubType,
          loanAmount: self.loanAmount,
          loanCurrency: self.loanCurrency,
          loanAmonthTHB: self.loanAmonthTHB || self.loanAmount,
          outStandingDebt: self.outStandingDebt || 0,
          outStandingDebtTHB: self.outStandingDebtTHB || 0,
          incomingDueDate: incomingDueDate,
          incomingDueAmount: self.incomingDueAmount,
          signDate: signDate,
          startDate: startDate,
          endDate: endDate,
          sourceType: self.sourceType || 'L',
          isGuarantee: self.isGuarantee || false,
          loanAge: self.loanAge || 0,
          counterparty: self.counterparty || ''
        };
        
        // สร้างข้อมูล AgreementTrans
        const agreementTransFormatted = {
          agreement: agreementData, // ใส่ reference ไปยัง agreement
          flowTypeCode: agreementTransData.agreementTrans.flowTypeCode,
          flowType: agreementTransData.agreementTrans.flowType || '',
          postingDate: formatDateToISO(agreementTransData.agreementTrans.postingDate) || new Date().toISOString(),
          amount: parseFloat(agreementTransData.agreementTrans.amount) || 0,
          gftrRefCode: agreementTransData.agreementTrans.gftrRefCode || self.gftrRefCode,
          description: agreementTransData.agreementTrans.description || '',
          status: agreementTransData.agreementTrans.status || 'ACTIVE',
          amountTHB: parseFloat(agreementTransData.agreementTrans.amountTHB) || parseFloat(agreementTransData.amount) || 0,
          curr: agreementTransData.agreementTrans.curr || self.loanCurrency || 'THB',
          isMapped: agreementTransData.agreementTrans.isMapped || false
        };
        
        // สร้าง payload สำหรับส่งไป API
        const payload = {
          agreement: agreementData,
          agreementTrans: agreementTransFormatted
        };
        
        console.log("last Payload: ", payload);
        // เรียกใช้ API เพื่อสร้างข้อมูลใหม่พร้อม Transaction
        const result = yield agreementServiceAPI.createAgreementWithTransaction(payload);
        
        // จัดการกับ response
        if (result && result.isCompleted) {
          // กรณีสำเร็จ
          self.alert.tigger = true;
          self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
          self.alert.message = "สร้างสัญญาอ้างอิงและธุรกรรมเรียบร้อยแล้ว";
          
          // อัพเดต agreementID ถ้ามีการ return กลับมาจาก API
          if (result && result.isCompleted) {
            console.log("API Response:", result);
            console.log("Result.data:", result.data);
            console.log("Data type:", typeof result.data);
            
            if (result.data) {
              // ถ้า data เป็นตัวเลข
              if (typeof result.data === 'number') {
                self.agreementID = result.data;
              }
              // ถ้า data เป็น object ที่มี agreementID
              else if (result.data.agreementID) {
                self.agreementID = result.data.agreementID;
              }
              // ถ้าเป็น array (กรณีพิเศษ)
              else if (Array.isArray(result.data) && result.data[0]!.agreementID) {
                self.agreementID = result.data[0].agreementID;
              }
            }
          }
          
          return result;
        } else {
          // กรณีไม่สำเร็จแต่ไม่มี error
          throw new Error("ไม่สามารถสร้างสัญญาอ้างอิงและธุรกรรมได้");
        }
      } catch (e) {
        // จัดการกับ error
        self.error.tigger = true;
        self.error.code = e.code || "ERROR";
        self.error.title = e.name || "Error";
        self.error.message = e.message || "เกิดข้อผิดพลาดในการสร้างสัญญาอ้างอิงและธุรกรรม";
        self.error.technicalStack = e.technicalStack || e.stack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    onAddAgreementToPlan: flow(function* (planActID: number) {
      try {
        self.loading = true;
        self.planType = self.planType && self.planType.id == 0 ? null : self.planType
        self.objective = self.objective && self.objective.id == 0 ? null : self.objective
        self.actualDueDate = self.actualDueDate
          ? self.actualDueDate
          : null
        if (planActID && self.agreementID) {
          const result = yield agreementServiceAPI.addAgreementToPlan(
            self.agreementID,
            planActID
          );
          self.alert.tigger = true;
          self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
          self.alert.message = "ข้อมูลถูกแก้ไขเรียบร้อยแล้ว";
        }
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    onAddAgreementToRestructurePlanByExistPlan: flow(function* (planExistIDs: number[], flowType: string, planId?: number) {
      try {
        self.loading = true;
        self.planType = self.planType && self.planType.id == 0 ? null : self.planType
        self.objective = self.objective && self.objective.id == 0 ? null : self.objective
        self.actualDueDate = self.actualDueDate ? self.actualDueDate : null
        if (self.agreementID) {
          for (const planExistID of Array.from(planExistIDs)) {
            yield agreementServiceAPI.addAgreementToRestructurePlanByExistPlan(
              self.agreementID,
              planExistID,
              flowType,
              planId
            );
          }
          self.alert.tigger = true;
          self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
          self.alert.message = "ข้อมูลถูกแก้ไขเรียบร้อยแล้ว";
        }
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    onAddAgreementToRestructurePlan: flow(function* (paymentPlanID: number, flowType: string) {
      try {
        self.loading = true;
        self.planType = self.planType && self.planType.id == 0 ? null : self.planType
        self.objective = self.objective && self.objective.id == 0 ? null : self.objective
        self.actualDueDate = self.actualDueDate
          ? self.actualDueDate
          : null
        if (paymentPlanID && self.agreementID) {
          const result = yield agreementServiceAPI.addAgreementToRestructurePlan(
            self.agreementID,
            paymentPlanID,
            flowType
          );
          self.alert.tigger = true;
          self.alert.title = "บันทึกข้อมูลสำเร็จแล้วค่ะ";
          self.alert.message = "ข้อมูลถูกแก้ไขเรียบร้อยแล้ว";
        }
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    removePlanActivities: flow(function* (agreementActID: number) {
      try {
        self.loading = true;
        const result = yield agreementServiceAPI.removePlanActivitiesById(
          agreementActID,
          self.agreementID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกสำเร็จค่ะ";
        self.alert.message = "ข้อมูลถูกลบเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    removeActualResultAgreement: flow(function* (planExistID: number, monthlyReportPlanId: number) {
      try {
        self.loading = true;
        yield monthlyReportAPI.deleteActualResultAgreement(
          self.agreementID,
          planExistID,
          monthlyReportPlanId
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกสำเร็จค่ะ";
        self.alert.message = "ข้อมูลถูกลบเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    removeRestructureplan: flow(function* (agreementActID: number) {
      try {
        self.loading = true;
        const result = yield agreementServiceAPI.removeRestructureplanById(
          agreementActID,
          self.agreementID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกสำเร็จค่ะ";
        self.alert.message = "ข้อมูลถูกลบเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    updateAgeement: flow(function* () {
      try {
        self.loading = true;
        const result = yield agreementServiceAPI.updateAgreement(
          self,
          self.agreementID
        );
        self.alert.tigger = true;
        self.alert.title = "บันทึกสำเร็จค่ะ";
        self.alert.message = "ข้อมูลถูกลบเรียบร้อยแล้ว";
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    setAllField: (data: any) => {
      Object.keys(data).forEach((key, index) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on key", key, e);
          throw e;
        }
      });
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    // setActualDueAmount(value: number) {
    //   self.actualDueAmount = value;
    // },
    setObjective: (value: IObjectiveModel) => {
      self.objective = value;
    },
    setPlanType: (value: IObjectiveModel) => {
      self.planType = value;
    },
    resetAll: () => {
      applySnapshot(self, {});
    },
    resetObjective: () => {
      self.objective = null;
    },
    resetPlanTypes: () => {
      self.planType = null;
    },
    onAddLoanSourcePlan: () => {
      self.actualDues.push({ sourceType: "F", currency: "USD",loanSourceID:null,thbAmount:0 });
    },
    onAddLoanSourcePlanDomestic: () => {
      self.actualDues.push({ sourceType: "L", currency: "THB" ,loanSourceID:null,thbAmount:0 });
      self.actualDues.push({ sourceType: "F", currency: "USD" ,loanSourceID:null,thbAmount:0  });
    },
    onRemoveLoanSourcePlan: (index: number) => {
      self.actualDues.splice(index + 1, 1);
    }
  }));
export type IAgreementDetailModel = typeof AgreementDetailModel.Type;
